/**
 * API 요청 인터셉터
 */
import { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import axios from "axios";

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

/**
 * 인증 토큰 인터셉터
 */
export const authInterceptor = {
    /**
     * 요청 전 처리
     * - API Secret 헤더 추가
     * - Access Token 추가 (필요 시)
     */
    onRequest: (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        // Access Token 추가 (세션 기반이므로 선택적)
        const accessTokenRaw = localStorage.getItem('accessToken');
        const accessToken = accessTokenRaw ? JSON.parse(accessTokenRaw) : null;

        if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
        }

        // 요청 로깅 제거 (필요시 주석 해제)
        // if (process.env.NODE_ENV === 'development') {
        //     console.log('[API Request]', {
        //         method: config.method?.toUpperCase(),
        //         url: config.url,
        //         data: config.data,
        //     });
        // }

        return config;
    },

    /**
     * 요청 에러 처리
     */
    onRequestError: (error: Error): Promise<Error> => {
        console.error('[API Request Error]', error);
        return Promise.reject(error);
    },

    /**
     * 응답 성공 처리
     */
    onResponse: (response: AxiosResponse): AxiosResponse => {
        // 응답 로깅 제거 (필요시 주석 해제)
        // if (process.env.NODE_ENV === 'development') {
        //     console.log('[API Response]', {
        //         url: response.config.url,
        //         status: response.status,
        //         data: response.data,
        //     });
        // }
        return response;
    },

    /**
     * 응답 에러 처리
     */
    onResponseError: async (error: any): Promise<any> => {
        const originalRequest = error.config;

        // 개발 환경에서만 에러 로깅
        if (process.env.NODE_ENV === 'development') {
            console.error('[API Response Error]', {
                url: error.config?.url,
                status: error.response?.status,
                data: error.response?.data
            });
        }

        // 세션 무효화 처리
        const errorCode = error.response?.data?.errorCode;
        if (errorCode === 'SESSION_INVALIDATED') {
            localStorage.clear();
            alert('다른 기기에서 로그인하여 현재 세션이 만료되었습니다.');
            window.location.href = '/login';
            return Promise.reject(error);
        }

        // 로그인 페이지에서는 토큰 갱신 시도하지 않음
        if (window.location.pathname === '/login') {
            return Promise.reject(error);
        }

        // Refresh API 자체의 401 에러는 재시도하지 않음 (무한 루프 방지)
        if (error.config.url?.includes('/auth/refresh')) {
            localStorage.clear();
            window.location.href = '/login';
            return Promise.reject(error);
        }

        // Access Token 만료 처리 (401 Unauthorized)
        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers['Authorization'] = 'Bearer ' + token;
                    return axios(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const refreshTokenRaw = localStorage.getItem('sessionId');
            const refreshToken = refreshTokenRaw ? JSON.parse(refreshTokenRaw) : null;

            if (!refreshToken) {
                isRefreshing = false;
                localStorage.clear();
                window.location.href = '/login';
                return Promise.reject(error);
            }

            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/api/v1/auth/refresh`,
                    { refreshToken },
                    {
                        headers: {}
                    }
                );

                const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;
                
                // 새로운 Access Token 저장
                localStorage.setItem('accessToken', JSON.stringify(newAccessToken));
                
                // 새로운 Refresh Token 저장 (Refresh Token Rotation)
                if (newRefreshToken) {
                    localStorage.setItem('refreshToken', JSON.stringify(newRefreshToken));
                }
                
                processQueue(null, newAccessToken);
                originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
                
                return axios(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                localStorage.clear();
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
}
